import React from "react";
import Page from "../UI/Page";
import {
  Box,
  Image,
  Text,
  Button,
  Link,
  VStack,
  Heading,
  Badge,
  HStack,
  Icon,
  Divider,
  Flex,
  useBreakpointValue,
  SimpleGrid,
} from "@chakra-ui/react";
import { ExternalLinkIcon, StarIcon, CheckCircleIcon } from "@chakra-ui/icons";
import IB from "../../assets/aff/ib.png";
import LG from "../../assets/aff/lg.png";

// ─── Constants ───────────────────────────────────────────────
const ACCENT_GRADIENT = "linear(to-r, #667eea, #764ba2)";

const tools = [
  {
    name: "Interactive Brokers",
    shortName: "IBKR",
    image: IB,
    description:
      "I use Interactive Brokers for stock and dividend investing. It provides access to global markets and a powerful trading platform.",
    link: "https://ibkr.com/referral/andreea340",
    category: "Investing",
    features: ["Global Markets", "Low Fees", "Advanced Tools"],
    recommended: true,
    gradient: "linear(to-br, #667eea, #764ba2)",
  },
  {
    name: "Ledger",
    shortName: "Ledger",
    image: LG,
    description:
      "I use Ledger to securely store my cryptocurrencies. With its hardware wallet technology, it keeps my assets safe from online threats.",
    link: "https://shop.ledger.com/pages/referral-program?referral_code=TB05HBV2A37NY",
    category: "Crypto Security",
    features: ["Hardware Wallet", "Multi-Currency", "Secure"],
    recommended: true,
    gradient: "linear(to-br, #f093fb, #f5576c)",
  },
];

// ─── Tool Card ───────────────────────────────────────────────
const ToolCard = ({ tool, index }) => {
  const isMobile = useBreakpointValue({ base: true, md: false });

  return (
    <Box
      sx={{
        animation: `fadeInUp 0.6s ease-out ${index * 0.2}s both`,
        "@keyframes fadeInUp": {
          from: { opacity: 0, transform: "translateY(40px)" },
          to: { opacity: 1, transform: "translateY(0)" },
        },
      }}
    >
      <Box
        position="relative"
        bg="linear-gradient(135deg, rgba(26, 32, 44, 0.95) 0%, rgba(45, 55, 72, 0.95) 100%)"
        borderRadius={{ base: "xl", md: "2xl" }}
        overflow="hidden"
        border="1px solid"
        borderColor="whiteAlpha.200"
        transition="all 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)"
        _hover={{
          transform: { base: "translateY(-4px)", md: "translateY(-8px)" },
          boxShadow: "0 20px 60px rgba(102, 126, 234, 0.4)",
          borderColor: "whiteAlpha.400",
        }}
        backdropFilter="blur(20px)"
        h="full"
      >
        {/* Gradient top border */}
        <Box
          position="absolute"
          top={0}
          left={0}
          right={0}
          height="3px"
          bgGradient={tool.gradient}
        />

        {/* Recommended badge */}
        {tool.recommended && (
          <Badge
            position="absolute"
            top={{ base: 2, md: 3 }}
            right={{ base: 2, md: 3 }}
            bg="linear-gradient(135deg, #FFD700, #FFA500)"
            color="gray.900"
            display="flex"
            alignItems="center"
            gap={1}
            px={{ base: 1.5, md: 2.5 }}
            py={{ base: 0.5, md: 1 }}
            borderRadius="full"
            fontSize="2xs"
            fontWeight="bold"
            boxShadow="0 4px 12px rgba(255, 215, 0, 0.4)"
            zIndex={2}
          >
            <Icon as={StarIcon} boxSize={2.5} />
            <Text display={{ base: "none", sm: "inline" }}>Recommended</Text>
            <Text display={{ base: "inline", sm: "none" }}>Top</Text>
          </Badge>
        )}

        {/* Card body */}
        <Box p={{ base: 3, sm: 4, md: 6, lg: 7 }}>
          <VStack spacing={{ base: 2.5, md: 5 }} align="stretch">
            {/* Logo & Title */}
            <HStack spacing={{ base: 2.5, md: 4 }} align="start">
              <Box
                bg="white"
                p={{ base: 1.5, md: 2.5 }}
                borderRadius="lg"
                boxShadow="0 4px 12px rgba(0, 0, 0, 0.2)"
                flexShrink={0}
              >
                <Image
                  src={tool.image}
                  alt={tool.name}
                  boxSize={{ base: "36px", sm: "45px", md: "55px" }}
                  objectFit="contain"
                />
              </Box>
              <VStack align="start" spacing={1} flex={1}>
                <Heading
                  size={{ base: "sm", md: "md" }}
                  color="white"
                  fontWeight="bold"
                  lineHeight="shorter"
                >
                  {tool.name}
                </Heading>
                <Badge
                  bgGradient={tool.gradient}
                  color="white"
                  fontSize="2xs"
                  px={2}
                  py={0.5}
                  borderRadius="full"
                  fontWeight="semibold"
                >
                  {tool.category}
                </Badge>
              </VStack>
            </HStack>

            <Divider borderColor="whiteAlpha.200" />

            {/* Description */}
            <Text
              fontSize={{ base: "xs", md: "sm" }}
              color="whiteAlpha.800"
              lineHeight={{ base: "1.6", md: "tall" }}
              fontWeight="500"
            >
              {tool.description}
            </Text>

            {/* Features */}
            <Box>
              <HStack mb={1.5} spacing={1.5}>
                <Icon as={CheckCircleIcon} color="teal.400" boxSize={3} />
                <Text
                  fontSize="2xs"
                  color="whiteAlpha.600"
                  fontWeight="bold"
                  letterSpacing="wide"
                >
                  KEY FEATURES
                </Text>
              </HStack>
              <Flex gap={1.5} flexWrap="wrap">
                {tool.features.map((feature, idx) => (
                  <Badge
                    key={idx}
                    bg="whiteAlpha.100"
                    color="whiteAlpha.900"
                    fontSize="2xs"
                    px={2}
                    py={0.5}
                    borderRadius="md"
                    border="1px solid"
                    borderColor="whiteAlpha.200"
                    fontWeight="600"
                    whiteSpace="nowrap"
                  >
                    {feature}
                  </Badge>
                ))}
              </Flex>
            </Box>

            {/* CTA */}
            <Link
              href={tool.link}
              isExternal
              _hover={{ textDecoration: "none" }}
              w="full"
              mt="auto"
            >
              <Button
                bgGradient={tool.gradient}
                color="white"
                w="full"
                size={{ base: "sm", md: "lg" }}
                height={{ base: "40px", md: "52px" }}
                rightIcon={<ExternalLinkIcon />}
                _hover={{
                  transform: "translateY(-2px)",
                  boxShadow: "0 12px 40px rgba(102, 126, 234, 0.5)",
                }}
                _active={{ transform: "translateY(0)" }}
                transition="all 0.3s ease"
                fontWeight="bold"
                fontSize={{ base: "xs", md: "md" }}
                borderRadius="lg"
              >
                Visit {isMobile ? tool.shortName : tool.name}
              </Button>
            </Link>

            {/* Affiliate note */}
            <HStack
              justify="center"
              spacing={1.5}
              px={2}
              py={1}
              bg="whiteAlpha.50"
              borderRadius="md"
            >
              <Text fontSize={{ base: "xs", md: "sm" }}>🔗</Text>
              <Text
                fontSize="2xs"
                color="whiteAlpha.600"
                fontStyle="italic"
                fontWeight="500"
              >
                Affiliate link - Supports Mattias
              </Text>
            </HStack>
          </VStack>
        </Box>
      </Box>
    </Box>
  );
};

// ─── Main Component ──────────────────────────────────────────
const MyToolkit = () => {
  return (
    <Page>
      <div className="hero" style={{ minHeight: "70vh" }}>
        <Box
          py={{ base: 4, md: 12 }}
          px={{ base: 3, md: 4 }}
          position="relative"
        >
          {/* Background glow — desktop only */}
          <Box
            position="absolute"
            top="0"
            left="50%"
            transform="translateX(-50%)"
            width="600px"
            height="600px"
            bgGradient="radial(circle, rgba(102, 126, 234, 0.15) 0%, transparent 70%)"
            filter="blur(60px)"
            pointerEvents="none"
            display={{ base: "none", lg: "block" }}
            sx={{
              animation: "pulse 4s ease-in-out infinite",
              "@keyframes pulse": {
                "0%, 100%": {
                  opacity: 0.5,
                  transform: "translateX(-50%) scale(1)",
                },
                "50%": {
                  opacity: 0.8,
                  transform: "translateX(-50%) scale(1.1)",
                },
              },
            }}
          />

          <VStack
            spacing={{ base: 5, md: 10 }}
            maxW="1200px"
            mx="auto"
            position="relative"
          >
            {/* Header */}
            <VStack
              spacing={{ base: 2, md: 4 }}
              mb={{ base: 0, md: 4 }}
              w="full"
              sx={{
                animation: "fadeInDown 0.6s ease-out",
                "@keyframes fadeInDown": {
                  from: { opacity: 0, transform: "translateY(-30px)" },
                  to: { opacity: 1, transform: "translateY(0)" },
                },
              }}
            >
              <Box
                p={{ base: 2.5, md: 4 }}
                bgGradient={ACCENT_GRADIENT}
                borderRadius="2xl"
                boxShadow="0 8px 32px rgba(102, 126, 234, 0.3)"
              >
                <Text fontSize={{ base: "2xl", md: "4xl" }}>🛠️</Text>
              </Box>

              <Heading
                size={{ base: "md", sm: "lg", md: "xl" }}
                textAlign="center"
                bgGradient="linear(to-r, #667eea, #764ba2, #f093fb)"
                bgClip="text"
                fontWeight="extrabold"
                letterSpacing="tight"
              >
                My Investment Toolkit
              </Heading>

              <Text
                fontSize={{ base: "xs", md: "sm" }}
                color="whiteAlpha.700"
                textAlign="center"
                maxW={{ base: "full", md: "2xl" }}
                lineHeight="tall"
                fontWeight="500"
                px={{ base: 2, md: 0 }}
              >
                Tools and platforms I personally use and recommend for building
                Mattias' portfolio. These are affiliate links that help support
                this project.
              </Text>
            </VStack>

            {/* Tools — side by side from sm */}
            <SimpleGrid
              columns={{ base: 1, sm: 2 }}
              spacing={{ base: 3, md: 8 }}
              w="full"
            >
              {tools.map((tool, index) => (
                <ToolCard key={tool.name} tool={tool} index={index} />
              ))}
            </SimpleGrid>

            {/* Footer Info */}
            <Box
              maxW="900px"
              w="full"
              mt={{ base: 1, md: 4 }}
              sx={{ animation: "fadeInUp 1s ease-out 0.4s both" }}
            >
              <Box
                bg="linear-gradient(135deg, rgba(99, 179, 237, 0.15) 0%, rgba(56, 178, 172, 0.15) 100%)"
                borderRadius={{ base: "xl", md: "2xl" }}
                border="1px solid"
                borderColor="whiteAlpha.200"
                backdropFilter="blur(20px)"
                overflow="hidden"
                position="relative"
              >
                {/* Animated gradient border */}
                <Box
                  position="absolute"
                  top={0}
                  left={0}
                  right={0}
                  height="2px"
                  bgGradient="linear(to-r, #63B3ED, #38B2AC, #63B3ED)"
                  bgSize="200% 100%"
                  sx={{
                    animation: "gradientShift 3s ease infinite",
                    "@keyframes gradientShift": {
                      "0%": { backgroundPosition: "0% 50%" },
                      "50%": { backgroundPosition: "100% 50%" },
                      "100%": { backgroundPosition: "0% 50%" },
                    },
                  }}
                />

                <Box p={{ base: 3, md: 5 }}>
                  <VStack spacing={{ base: 2, md: 3 }}>
                    <Box
                      p={{ base: 1.5, md: 2.5 }}
                      bg="linear-gradient(135deg, #63B3ED, #38B2AC)"
                      borderRadius="xl"
                      boxShadow="0 8px 20px rgba(99, 179, 237, 0.3)"
                    >
                      <Text fontSize={{ base: "lg", md: "2xl" }}>💡</Text>
                    </Box>

                    <Heading
                      size={{ base: "xs", md: "md" }}
                      color="white"
                      fontWeight="bold"
                    >
                      Why These Tools?
                    </Heading>

                    <Text
                      fontSize={{ base: "xs", md: "sm" }}
                      color="whiteAlpha.800"
                      textAlign="center"
                      lineHeight={{ base: "1.6", md: "tall" }}
                      maxW={{ base: "full", md: "700px" }}
                      fontWeight="500"
                    >
                      I've personally tested and use these platforms for Mattias'
                      investment portfolio. They offer the best combination of
                      security, features, and value.
                      <Text
                        as="span"
                        display={{ base: "none", sm: "inline" }}
                      >
                        {" "}Using these affiliate links helps fund future
                        investments while costing you nothing extra.
                      </Text>
                    </Text>

                    <Flex
                      gap={{ base: 3, md: 4 }}
                      pt={1}
                      flexWrap="wrap"
                      justify="center"
                    >
                      {["Tested", "Secure", "Best Value"].map((label) => (
                        <HStack key={label} spacing={1}>
                          <Icon
                            as={CheckCircleIcon}
                            color="teal.400"
                            boxSize={3}
                          />
                          <Text
                            fontSize="2xs"
                            color="whiteAlpha.700"
                            fontWeight="semibold"
                          >
                            {label}
                          </Text>
                        </HStack>
                      ))}
                    </Flex>
                  </VStack>
                </Box>
              </Box>
            </Box>
          </VStack>
        </Box>
      </div>
    </Page>
  );
};

export default MyToolkit;